import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ApiService } from "@/services/api-service";
import useAuthStore from "@/store/useAuthStore";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertCircle, Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/auth/_layout/login-admin")({
  component: LoginAdmin,
});

function LoginAdmin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const authRes = await ApiService.loginAdmin({
        email,
        password,
      });

      login(authRes.user, {
        token: authRes.authTokens.token,
        refreshToken: null,
      });

      navigate({
        to: "/admin",
      });
    } catch (err) {
      console.log(err);
      const errMsg = (err as any)?.response?.data?.message || "Login Failed";
      setError(errMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <div className="max-w-md w-full">
        <Card className=" shadow-2xl rounded-4xl bg-neutral-50">
          <Link to="/" className="flex justify-center mb-1">
            <img src="/logo/inkriv-logo-dark.svg" alt="logo" className="h-12" />
          </Link>
          <CardContent className="pt-6">
            <form onSubmit={handleLogin} className="space-y-4 mb-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium mb-1 font-satoshi"
                >
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-neutral-25 dark:bg-neutral-950 pr-10 text-xs placeholder:text-[12px] sm:placeholder:text-xs font-satoshi"
                />
              </div>
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium mb-1 font-satoshi"
                >
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-neutral-25 dark:bg-neutral-950 pr-10 text-xs placeholder:text-[12px] sm:placeholder:text-xs font-satoshi"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-4 transform -translate-y-1/2"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center gap-2 font-satoshi"
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Login"
                )}
              </Button>
            </form>
          </CardContent>
          {error && (
            <CardFooter className="flex flex-col pt-0 relative max-w-lg  rounded-b-2xl">
              {error && (
                <Alert variant="destructive" className="mt-4">
                  <AlertCircle />
                  <AlertDescription className="text-xs sm:text-sm ">
                    {error}
                  </AlertDescription>
                </Alert>
              )}
            </CardFooter>
          )}
        </Card>
      </div>
    </>
  );
}
